import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  updateUserInfo,
  signupAsync,
  selectAccountSetup,
} from "@/store/redux/slices/auth/auth_slice";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import Button from "@/components/common/Button";
import OAuthButtons from "@/components/shared/OAuth_Buttons/OAuth_Buttons";
import CountryFlag from "@/components/common/Country_Flag";
import { ArrowRightOutlined } from "@/config/antd";
import Success from "@/components/common/alerts/Success";
import WebIcon from "@/components/common/Web_Icons";
import logo from "@/assets/icons/web_Icons/veegil_logo.svg";
import Error from "@/components/common/alerts/Error";

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useSelector(selectAccountSetup);
  const { showLoader, hideLoader } = useLoader();
  const [fullname, setFullname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFullNameChange = (e) => setFullname(e.target.value);
  const handlePhoneNumberChange = (e) => setPhone(e.target.value);
  const handleEmailChange = (e) => setEmail(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);

  const isFormFilled =
    fullname.trim() !== "" &&
    phone.trim() !== "" &&
    email.trim() !== "" &&
    password.trim() !== "";

  const handleProceed = async () => {
    // Form validation
    if (!isFormFilled) {
      setErrorMessage("Please fill in all required fields");
      setIsErrorVisible(true);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address");
      setIsErrorVisible(true);
      return;
    }

    // Password validation (minimum 6 characters)
    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters long");
      setIsErrorVisible(true);
      return;
    }

    showLoader();
    try {
      const userData = {
        fullname,
        phone,
        email,
        password,
      };

      const registrationResult = await dispatch(signupAsync(userData)).unwrap();

      dispatch(
        updateUserInfo({
          fullname,
          phone,
          email,
          userID: registrationResult?.userID,
        })
      );

      setIsSuccessVisible(true);
    } catch (error) {
      const errorText =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "";
      const isNetworkFailure =
        /network error/i.test(String(errorText)) ||
        error?.code === "ERR_NETWORK" ||
        error?.name === "AxiosError";

      // Global error handler already shows the Network modal for CORS/offline.
      if (isNetworkFailure) {
        setIsSuccessVisible(false);
        return;
      }

      let userFriendlyMessage = "Failed to create account. Please try again.";

      if (error?.message?.includes("expected `email` to be unique")) {
        userFriendlyMessage =
          "An account with this email already exists. Please sign in or use a different email address.";
      } else if (error?.message?.includes("phone")) {
        userFriendlyMessage =
          "This phone number is already registered. Please use a different number.";
      } else if (error?.statusCode === 406) {
        userFriendlyMessage =
          "The information provided is not valid. Please check your details and try again.";
      }

      setErrorMessage(userFriendlyMessage);
      setIsErrorVisible(true);
      setIsSuccessVisible(false);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleContinue = () => {
    showLoader();
    // Show loader briefly, then hide success modal and navigate to OTP screen
    setTimeout(() => {
      hideLoader();
      setIsSuccessVisible(false);
      navigate("/otp");
    }, 2000);
  };

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  //  // Clear error when component unmounts or when user retries
  // useEffect(() => {
  //   return () => {
  //     dispatch(clearError());
  //   };
  // }, [dispatch]);

  return (
    <div className="flex flex-col" data-testid="signup-container">
      {/* Top section */}
      <div className="flex justify-between items-center p-4 md:p-6">
        <div
          className="logo-container flex items-center"
          data-testid="logo-container">
          <img
            src={logo}
            alt="Veegil Logo"
            className="logo-image w-[53px] h-[44px]"
          />
          <span className="text-[24px] text-primary font-roboto-slab font-normal">
            Veegil
          </span>
        </div>
        <Link
          to="/signin"
          className="text-bold text-primary font-bold hover:text-hover"
          data-testid="signin-link">
          Sign In
        </Link>
      </div>

      {/* Middle section */}
      <div className="flex flex-col px-4 md:px-6 max-w-md mx-auto w-full">
        <h1
          className="heading-large whitespace-nowrap text-left text-primary mb-4"
          data-testid="setup-account-heading">
          Setup Account
        </h1>
        <p
          className="text-small text-left text-darkGrey mb-6 w-full"
          data-testid="welcome-text">
          Welcome to our service. We&apos;re thrilled that you&apos;re
          interested in using our service.
        </p>

        <div className="mb-4">
          <Input
            data-testid="fullname-input"
            label="FULL NAME"
            placeholder="Enter your full name"
            icon={<WebIcon icon="Profile" size="small" />}
            type="text"
            value={fullname}
            onChange={handleFullNameChange}
          />
        </div>

        <div className="mb-4">
          <Input
            data-testid="phone-input"
            label="PHONE NUMBER"
            placeholder="Enter your phone number"
            icon={<CountryFlag />}
            type="tel"
            value={phone}
            onChange={handlePhoneNumberChange}
          />
        </div>

        <div className="mb-4">
          <Input
            data-testid="email-input"
            label="EMAIL ADDRESS"
            placeholder="Enter your email address"
            icon={<WebIcon icon="Message" size="small" />}
            type="email"
            value={email}
            onChange={handleEmailChange}
          />
        </div>

        <div className="mb-4">
          <Input
            data-testid="password-input"
            label="PASSWORD"
            placeholder="Enter your password"
            icon={<WebIcon icon="Lock" size="small" />}
            type="password"
            value={password}
            onChange={handlePasswordChange}
          />
        </div>

        <div>
          <Button
            data-testid="proceed-button"
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={isFormFilled}
            color="darkBlue"
            className={`w-full ${
              isFormFilled
                ? "bg-primary text-white"
                : "bg-darkBlue text-primary"
            }`}
          />
        </div>

        <div className="mt-6 text-left">
          <p
            className="mb-4 font-bold text-black text-small"
            data-testid="signup-using-text">
            Sign up using
          </p>
          {/* OAuth buttons */}
          <OAuthButtons />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Account Created"
        paragraph='We sent an OTP to your email address. Click "Continue" to complete your account setup.'
        buttonText="Continue"
        showCancelButton={true}
        cancelButtonText="Close and continue"
        recoveryPath="/otp"
        onButtonClick={handleContinue}
      />

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Sign Up Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/signup"
        linkText="Already have an account?"
        linkPath="/signin"
      />
    </div>
  );
};

export default SignUp;
